import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ViewMasterService } from 'src/app/services/view-master/view-master.service';
import { ViewMasterModalService } from 'src/app/services/view-master-modal/view-master-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-add-master',
  templateUrl: './md-add-master.component.html',
  styleUrls: ['./md-add-master.component.scss']
})
export class MdAddMasterComponent extends DestructorComponent implements OnInit {

  @Output() viewMasterAdded = new EventEmitter<void>();
  @Output() viewMasterUpdated = new EventEmitter<void>();

  viewMasterForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedViewMaster: any = null;

  // Vehicle fields options
  vehicleFields = [
    "Arrival port", "Auction date", "Auction Name", "Avg Ex Rate", "Balance Amount",
    "Balance Finalized Date", "Book Req. Date", "Booking Status", "Buyer Name", "Car Category",
    "CC", "Charges", "Chassis Number", "Color", "Consignee Address1", "Consignee City",
    "Consignee Country", "Consignee Email", "Consignee Name", "Consignee Tel1", "Consignee Tel2",
    "Container Number", "CTN Group No.", "Currency", "Departing Port", "Doors", "Drive",
    "Engine Model", "ETA", "ETD", "Ext. Grade", "Freight Rate", "Freight Amount", "Fuel",
    "Height (cm)", "Insp. Status", "Inv. Amount", "Invoice Total", "Length (cm)",
    "Load Capacity(kg)", "Lot Number", "M3", "Make", "Maker Grade", "Masho Date",
    "Master BOL No. (BL Number)", "Mfg Month", "Mfg Year", "Mileage (km)", "Model",
    "Model Code", "Notify Party Address1", "Notify Party City", "Notify Party Country",
    "Notify Party Email", "Notify Party Name", "Notify Party Tel1", "Paid Amount",
    "Reg Month", "Report Date", "Sailing Country", "Sales Agent", "Seat", "Ship Name",
    "Ship Type", "Shipping Company", "Sold Date", "Steering", "Terms", "Trans",
    "Voyage No", "Weight (kg)", "Width (cm)", "Yard", "Yard In Date", "Year"
  ];

  // Charges options
  charges = [
    "1st Transport Charge", "2nd Transport Charge", "3rd Transport Charge",
    "Additional Fee \"Rikuso Co.,\"", "Additional Inspection Fee", "ATJ PSI", "Auc Price Tax",
    "Auction Penalty", "Auction Price", "Auction Price + Tax", "Automobile Tax", "B/L Charges",
    "Bid charge", "Bid Charge + Tax", "Bid Charge Tax", "Booking Agent Commision",
    "Car Insurance", "CAT FEE", "Cleaning Fee", "Collection Fee", "Consumption tax",
    "Custom / Shipping Charge", "Cutting CKD Fees", "DHL Docs Fee", "Doc Fee", "Drage - CTN",
    "EAA", "EMS", "Engine Number", "EXTRA PARTS", "FEDEX", "Fuel Charge",
    "Gas Removal Charge", "Heat Treatment (FJI)", "Inner Cargo Charge", "ISL DOCS FEE",
    "Issuing Bank Fee", "JEVIC", "LC Ammendment Fee", "LC Cable Charge", "Marine Insurance",
    "MB INSP CANCEL FEE", "Meigihekein Deposit", "Mileage repair", "Ocean Freight", "Parts",
    "Parts Disposal Fee", "Photo Shooting Fee", "Pre-Export Inspection", "QISJ",
    "Radiation Check Fee", "Re-Auc Sold Price", "Recycle Fee PAID", "Sales Commission",
    "Seal Fee", "Selling Charge shuppin", "Shipping Extra Costs", "Sold Charge Seyaku",
    "Stamp Fee", "SURRENDER FEE", "THC", "Transfer Fee", "Vanning", "Vehicle Repair",
    "Weight Tax", "Yard to Port YOKOMOCHI", "Yard-Out Penalty", "Yen Exchange", "YARD STORAGE FEE"
  ];

  constructor(
    private fb: FormBuilder,
    private _viewMasterService: ViewMasterService,
    private _viewMasterModalService: ViewMasterModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.viewMasterForm = this.fb.group({
      name: ['', [Validators.required]],
      vehicle_fields: this.fb.array([]),
      charges: this.fb.array([])
    });

    // Initialize checkbox arrays
    this.initializeVehicleFieldsCheckboxes();
    this.initializeChargesCheckboxes();
  }

  private initializeVehicleFieldsCheckboxes(): void {
    const vehicleFieldsArray = this.viewMasterForm.get('vehicle_fields') as FormArray;
    this.vehicleFields.forEach(() => {
      vehicleFieldsArray.push(this.fb.control(false));
    });
  }

  private initializeChargesCheckboxes(): void {
    const chargesArray = this.viewMasterForm.get('charges') as FormArray;
    this.charges.forEach(() => {
      chargesArray.push(this.fb.control(false));
    });
  }

  get vehicleFieldsFormArray(): FormArray {
    return this.viewMasterForm.get('vehicle_fields') as FormArray;
  }

  get chargesFormArray(): FormArray {
    return this.viewMasterForm.get('charges') as FormArray;
  }

  private setupModalListener(): void {
    this.subscriptions.push(
      this._viewMasterModalService.selectedViewMaster$.subscribe(viewMaster => {
        if (viewMaster) {
          this.isEditMode = true;
          this.selectedViewMaster = viewMaster;
          this.populateForm(viewMaster);
        } else {
          this.isEditMode = false;
          this.selectedViewMaster = null;
          this.resetForm();
        }
      })
    );
  }

  private populateForm(viewMaster: any): void {
    this.viewMasterForm.patchValue({
      name: viewMaster.name || ''
    });

    // Set vehicle fields checkboxes
    if (viewMaster.vehicle_fields && Array.isArray(viewMaster.vehicle_fields)) {
      const vehicleFieldsArray = this.vehicleFieldsFormArray;
      this.vehicleFields.forEach((field, index) => {
        const isSelected = viewMaster.vehicle_fields.includes(field);
        vehicleFieldsArray.at(index).setValue(isSelected);
      });
    }

    // Set charges checkboxes
    if (viewMaster.charges && Array.isArray(viewMaster.charges)) {
      const chargesArray = this.chargesFormArray;
      this.charges.forEach((charge, index) => {
        const isSelected = viewMaster.charges.includes(charge);
        chargesArray.at(index).setValue(isSelected);
      });
    }
  }

  private resetForm(): void {
    this.viewMasterForm.reset();
    this.initializeForm();
  }

  onSubmit(): void {
    if (this.viewMasterForm.invalid) {
      this.markFormGroupTouched(this.viewMasterForm);
      return;
    }

    this.isLoading = true;

    const formData = this.prepareFormData();

    if (this.isEditMode) {
      this.updateViewMaster(formData);
    } else {
      this.addViewMaster(formData);
    }
  }

  private prepareFormData(): any {
    const formValue = this.viewMasterForm.value;

    // Get selected vehicle fields
    const selectedVehicleFields = this.vehicleFields.filter((field, index) =>
      formValue.vehicle_fields[index]
    );

    // Get selected charges
    const selectedCharges = this.charges.filter((charge, index) =>
      formValue.charges[index]
    );

    return {
      name: formValue.name,
      vehicle_fields: selectedVehicleFields,
      charges: selectedCharges
    };
  }

  private addViewMaster(formData: any): void {
    this.subscriptions.push(
      this._viewMasterService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'View master added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.viewMasterAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add view master.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error adding view master:', error);
          this.isLoading = false;
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while adding the view master.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateViewMaster(formData: any): void {
    const viewMasterId = this.selectedViewMaster._id || this.selectedViewMaster.id;
    this.subscriptions.push(
      this._viewMasterService.UpdateData(viewMasterId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'View master updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.viewMasterUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update view master.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error updating view master:', error);
          this.isLoading = false;
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while updating the view master.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_view_master_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
    this.resetForm();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.viewMasterForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.viewMasterForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
      }
    }
    return '';
  }
}
